// =============================================================================
// src/features/analytics/analytics-mock-data.ts - Mundo mockado Omar Aziz.
// =============================================================================

import {
  OMAR_ANALYTICS_DAILY_CHART_SAMPLES,
  OMAR_ANALYTICS_MONTHLY_CHART_SAMPLES,
  OMAR_ANALYTICS_WEEKLY_CHART_SAMPLES,
} from './analytics-chart-samples';
import { OMAR_ANALYTICS_MONTH_TOTALS } from './analytics-month-totals';
import {
  ANALYTICS_KPI_INTERACTIONS_LABEL,
  ANALYTICS_KPI_MENTIONS_LABEL,
} from './analytics-types';
import type {
  OmarAnalyticsChartPoint,
  OmarAnalyticsChartSeries,
  OmarAnalyticsComparisonMetric,
  OmarAnalyticsDirectoryMention,
  OmarAnalyticsFilterSets,
  OmarAnalyticsInfluencer,
  OmarAnalyticsKpi,
  OmarAnalyticsMention,
  OmarAnalyticsPlatformCount,
  OmarAnalyticsSecondaryModule,
  OmarAnalyticsSentimentPoint,
  OmarAnalyticsSite,
} from './analytics-types';

export const OMAR_ANALYTICS_KPIS: readonly OmarAnalyticsKpi[] = [
  { label: ANALYTICS_KPI_MENTIONS_LABEL, value: OMAR_ANALYTICS_MONTH_TOTALS.mentionsLabel, delta: '+22%', trend: 'up', context: 'crescimento em torno da BR-319' },
  { label: 'Alcance estimado', value: OMAR_ANALYTICS_MONTH_TOTALS.reachLabel, delta: '+14%', trend: 'up', context: 'picos em Manaus e interior' },
  { label: ANALYTICS_KPI_INTERACTIONS_LABEL, value: OMAR_ANALYTICS_MONTH_TOTALS.interactionsLabel, delta: '+31%', trend: 'up', context: 'TikTok concentrou 76% do engajamento' },
  { label: 'Sentimento', value: 'Neutro', delta: 'estavel', trend: 'flat', context: OMAR_ANALYTICS_MONTH_TOTALS.sentimentLabel },
];

export const OMAR_ANALYTICS_PLATFORM_COUNTS: readonly OmarAnalyticsPlatformCount[] = [
  { platform: 'Todos', iconName: 'ti-layout-grid', count: OMAR_ANALYTICS_MONTH_TOTALS.mentions },
  { platform: 'X/Twitter', iconName: 'ti-brand-x', count: 512 },
  { platform: 'Facebook', iconName: 'ti-brand-facebook', count: 318 },
  { platform: 'Instagram', iconName: 'ti-brand-instagram', count: 384 },
  { platform: 'TikTok', iconName: 'ti-brand-tiktok', count: OMAR_ANALYTICS_MONTH_TOTALS.tiktokMentions },
  { platform: 'Noticias', iconName: 'ti-news', count: 486 },
  { platform: 'Blogs', iconName: 'ti-message-2', count: 198 },
  { platform: 'Web', iconName: 'ti-world', count: 124 },
];

export const OMAR_ANALYTICS_CHART_SERIES: readonly OmarAnalyticsChartSeries[] = [
  { granularity: 'days', label: 'Dias', points: OMAR_ANALYTICS_DAILY_CHART_SAMPLES },
  { granularity: 'weeks', label: 'Semanas', points: OMAR_ANALYTICS_WEEKLY_CHART_SAMPLES },
  { granularity: 'months', label: 'Meses', points: OMAR_ANALYTICS_MONTHLY_CHART_SAMPLES },
];

export const OMAR_ANALYTICS_CHART_POINTS: readonly OmarAnalyticsChartPoint[] = OMAR_ANALYTICS_DAILY_CHART_SAMPLES.map((chartSample) => ({
  label: chartSample.label,
  mentions: chartSample.mentions,
  reach: chartSample.reach,
}));

export const OMAR_ANALYTICS_SENTIMENT_POINTS: readonly OmarAnalyticsSentimentPoint[] = OMAR_ANALYTICS_DAILY_CHART_SAMPLES.map((chartSample) => ({
  label: chartSample.label,
  positive: chartSample.positive,
  neutral: chartSample.neutral,
  negative: chartSample.negative,
}));

const LUIZ_BACCI_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'luiz-bacci-instagram',
    dateLabel: '20 jun',
    sourceName: 'Luiz Bacci',
    platform: 'Instagram',
    sentiment: 'neutral',
    emotion: 'Atencao',
    score: '10/10',
    reach: '1,2 mi',
    title: 'Post citando Omar Aziz gera 3.471 comentarios',
    excerpt: 'Citou o senador no contexto de agenda federal e visita presidencial ao Amazonas, com audiencia massiva.',
  },
  {
    mentionId: 'luiz-bacci-br319',
    dateLabel: '22 jun',
    sourceName: 'Luiz Bacci',
    platform: 'Instagram',
    sentiment: 'neutral',
    emotion: 'Atencao',
    score: '9/10',
    reach: '840 mil',
    title: 'BR-319 entra no debate nacional via Instagram',
    excerpt: 'A publicacao deu escala nacional a uma pauta local, favorecendo leitura de articulacao em Brasilia.',
  },
];

const CENARIUM_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'cenarium-tiktok-pico',
    dateLabel: '22 jun',
    sourceName: 'Cenarium',
    platform: 'TikTok',
    sentiment: 'neutral',
    emotion: 'Atencao',
    score: '9/10',
    reach: '124 mil',
    title: 'Recortes sobre BR-319 ditam ritmo no pico',
    excerpt: 'Midia local em formato curto amplificou cobranca por cronograma e presenca federal no Amazonas.',
  },
];

const CONVERGENTE_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'convergente-tiktok',
    dateLabel: '19 jun',
    sourceName: 'Portal O Convergente',
    platform: 'TikTok',
    sentiment: 'neutral',
    emotion: 'Atencao',
    score: '8/10',
    reach: '86 mil',
    title: 'Portal regional acelera pauta territorial',
    excerpt: 'Combinou noticias e video curto sobre Omar Aziz e interiorizacao da agenda.',
  },
];

const NORTE_DIGITAL_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'norte-digital-sucessao',
    dateLabel: '25 jun',
    sourceName: 'Norte Digital',
    platform: 'TikTok',
    sentiment: 'negative',
    emotion: 'Raiva',
    score: '9/10',
    reach: '186 mil',
    title: 'Video curto pressiona narrativa de sucessao politica',
    excerpt: 'O recorte mistura idade, renovacao e disputa de poder, com alto potencial de remix entre jovens de Manaus.',
  },
  {
    mentionId: 'norte-digital-br319',
    dateLabel: '22 jun',
    sourceName: 'Norte Digital',
    platform: 'TikTok',
    sentiment: 'negative',
    emotion: 'Ataque',
    score: '8/10',
    reach: '94 mil',
    title: 'BR-319 vira gancho para cobranca direta',
    excerpt: 'A mencao simplifica a pauta tecnica em promessa nao cumprida, exigindo resposta visual curta e verificavel.',
  },
];

const BASE_ZONA_LESTE_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'base-zona-leste-obras',
    dateLabel: '26 jun',
    sourceName: 'Base Zona Leste',
    platform: 'X/Twitter',
    sentiment: 'positive',
    emotion: 'Apoio',
    score: '8/10',
    reach: '31 mil',
    title: 'Perfis comunitarios amplificam fala sobre acesso ao interior',
    excerpt: 'A conversa sai do tom institucional e vira linguagem de bairro, com boa aceitacao em bases locais.',
  },
];

const RADIO_CIDADE_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'radio-cidade-saude',
    dateLabel: '25 jun',
    sourceName: 'Radio Cidade FM',
    platform: 'Facebook',
    sentiment: 'neutral',
    emotion: 'Atencao',
    score: '6/10',
    reach: '112 mil',
    title: 'Entrevista preserva pauta de saude sem crise imediata',
    excerpt: 'A audiencia pede prazos e status de encaminhamentos, mas ainda responde melhor a prestacao de contas.',
  },
];

const PANORAMA_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'panorama-interior',
    dateLabel: '24 jun',
    sourceName: 'Blog Panorama AM',
    platform: 'Blogs',
    sentiment: 'positive',
    emotion: 'Confianca',
    score: '7/10',
    reach: '42 mil',
    title: 'Aliados defendem vantagem na pauta territorial',
    excerpt: 'A leitura reforca o elo entre capital, interior e Senado, boa para distribuicao em redes politicas locais.',
  },
];

const PORTAL_HOLANDA_DIRECTORY_MENTIONS: readonly OmarAnalyticsDirectoryMention[] = [
  {
    mentionId: 'portal-holanda-br319',
    dateLabel: '26 jun',
    sourceName: 'Portal do Holanda',
    platform: 'Noticias',
    sentiment: 'negative',
    emotion: 'Raiva',
    score: '10/10',
    reach: '420 mil',
    title: 'BR-319 puxa comentarios de cobranca e promessas antigas',
    excerpt: 'A materia concentra perguntas sobre cronograma, isolamento do interior e responsabilidade federal.',
  },
  {
    mentionId: 'portal-holanda-sucessao',
    dateLabel: '23 jun',
    sourceName: 'Portal do Holanda',
    platform: 'Noticias',
    sentiment: 'negative',
    emotion: 'Ataque',
    score: '8/10',
    reach: '180 mil',
    title: 'Sucessao estadual aparece junto de infraestrutura',
    excerpt: 'O enquadramento mistura disputa politica e obras, aumentando o risco de contaminacao entre pautas.',
  },
];

export const OMAR_ANALYTICS_FEED_ITEMS: readonly OmarAnalyticsMention[] = [
  {
    mentionId: 'br319-pico-22jun',
    author: 'Cenarium',
    authorHandle: '@cenariumam',
    authorReach: '412 mil seguidores',
    platform: 'TikTok',
    time: '22 jun, 10h32',
    sentiment: 'neutral',
    reach: '124 mil',
    score: '9/10',
    interactions: '42,8 mil',
    comments: '2,1 mil',
    shares: '3,4 mil',
    emotion: 'Atencao',
    title: 'Maior pico do mes combina interior, BR-319 e pesquisa',
    text: 'Conteudos do interior e da BR-319 coincidiram com novas leituras de pesquisa no maior volume diario.',
    sourceUrlLabel: 'tiktok.com/@cenariumam/video/pico22jun',
    mediaKind: 'video',
    recentRank: 1,
    popularRank: 1,
    detailLead: 'O dia 22 junho concentrou o maior volume do recorte quando estrada, eleicao e interior convergiram.',
    detailSummary: 'TikTok liderou engajamento com 76% do total em apenas 85 mencoes no mes.',
    detailRisk: 'Sem resposta em 2h, remix negativo contamina Instagram e X antes do fim da tarde.',
    detailAction: 'Publicar corte vertical de 20 a 40 segundos com mapa simples e cronograma cobrado.',
    detailSignals: ['maior pico diario', 'TikTok lidera engajamento', 'pesquisa altera clima'],
    recommendedActions: ['Gravar resposta vertical', 'Ativar carrossel visual', 'Monitorar comentarios de Luiz Bacci'],
    tags: ['BR-319', 'TikTok', 'Interior'],
  },
  {
    mentionId: 'luiz-bacci-instagram',
    author: 'Luiz Bacci',
    authorHandle: '@luizbacci',
    authorReach: '8,8 mi seguidores',
    platform: 'Instagram',
    time: '20 jun, 09h44',
    sentiment: 'neutral',
    reach: '1,2 mi',
    score: '10/10',
    interactions: '86,4 mil',
    comments: '3.471',
    shares: '4,2 mil',
    emotion: 'Atencao',
    title: 'Post citando Omar Aziz gera comentarios massivos',
    text: 'Citou Omar Aziz em contexto de agenda federal e visita presidencial, com 3.471 comentarios.',
    sourceUrlLabel: 'instagram.com/p/luizbacci-omar',
    mediaKind: 'post',
    recentRank: 2,
    popularRank: 2,
    detailLead: 'Instagram trouxe audiencia massiva via perfil nacional com comentarios volumosos.',
    detailSummary: 'Item de maior alcance fora do TikTok no recorte 23 mai – 23 jun.',
    detailRisk: 'Comentarios nao respondidos podem migrar tom neutro para polarizado.',
    detailAction: 'Responder comentarios de maior alcance nas proximas 2h com nota factual.',
    detailSignals: ['3.471 comentarios', 'alcance nacional', 'tom neutro'],
    recommendedActions: ['Responder top comentarios', 'Preparar nota para portais', 'Evitar tom defensivo'],
    tags: ['Instagram', 'Nacional', 'BR-319'],
  },
  {
    mentionId: 'globo-br319',
    author: 'O Globo',
    authorHandle: '@oglobo',
    authorReach: '12 mi seguidores',
    platform: 'Noticias',
    time: '21 jun, 18h10',
    sentiment: 'neutral',
    reach: '380 mil',
    score: '8/10',
    interactions: '12,4 mil',
    comments: '420',
    shares: '890',
    emotion: 'Atencao',
    title: 'Cobertura nacional sobre BR-319 e agenda amazonense',
    text: 'O Globo conectou estrada, eleicao e presenca federal no Amazonas com tom de acompanhamento.',
    sourceUrlLabel: 'oglobo.globo.com/brasil/br319-amazonas',
    mediaKind: 'noticia',
    recentRank: 3,
    popularRank: 3,
    detailLead: 'Portais nacionais deram escala a uma pauta que comecou local em Manaus.',
    detailSummary: 'Cobertura neutra com potencial de amplificar cobranca por cronograma federal.',
    detailRisk: 'Enquadramento nacional pode misturar sucessao e infraestrutura sem contraponto.',
    detailAction: 'Enviar nota objetiva com tres provas: cronograma, impacto logistico e valor local.',
    detailSignals: ['portal nacional', 'tom neutro', 'cobranca por prazos'],
    recommendedActions: ['Nota para redacao nacional', 'Card com linha do tempo', 'Mapa de impacto'],
    tags: ['BR-319', 'Nacional', 'Noticias'],
  },
  {
    mentionId: 'tiktok-omar-br319',
    author: 'Omar Aziz',
    authorHandle: '@omaraziz',
    authorReach: '186 mil seguidores',
    platform: 'TikTok',
    time: '19 jun, 13h27',
    sentiment: 'neutral',
    reach: '94 mil',
    score: '9/10',
    interactions: '28,6 mil',
    comments: '1,4 mil',
    shares: '2,1 mil',
    emotion: 'Apoio',
    title: 'Video alterna celebracao de obras e cobranca objetiva',
    text: 'Principal motor de interacoes do mes em TikTok, com tom misto de entrega e cobranca federal.',
    sourceUrlLabel: 'tiktok.com/@omaraziz/video/br319',
    mediaKind: 'video',
    recentRank: 4,
    popularRank: 4,
    detailLead: 'Omar Aziz como protagonista no TikTok com videos que puxaram o maior engajamento.',
    detailSummary: 'Formato curto funcionou melhor que notas longas para sustentar leitura territorial.',
    detailRisk: 'Videos sem atualizacao frequente perdem tracao frente a criticos em remix.',
    detailAction: 'Manter cadencia de 20 a 40 segundos alinhada a eventos publicos.',
    detailSignals: ['protagonista TikTok', 'engajamento alto', 'tom misto'],
    recommendedActions: ['Agendar proximos cortes', 'Usar antes-depois visual', 'Responder comentarios-chave'],
    tags: ['TikTok', 'Omar Aziz', 'BR-319'],
  },
  {
    mentionId: 'radar-pesquisa',
    author: 'Radar Amazonico',
    authorHandle: '@radaramazonico',
    authorReach: '64 mil seguidores',
    platform: 'X/Twitter',
    time: '5 jun, 16h02',
    sentiment: 'neutral',
    reach: '42 mil',
    score: '7/10',
    interactions: '4,2 mil',
    comments: '128',
    shares: '340',
    emotion: 'Atencao',
    title: 'Pesquisa local altera temperatura da conversa',
    text: 'Leituras de intencao de voto entraram no feed com Radar Amazonico citando Omar e o interior.',
    sourceUrlLabel: 'x.com/radaramazonico/status/pesquisa',
    mediaKind: 'post',
    recentRank: 5,
    popularRank: 5,
    detailLead: 'Pesquisas mudam o clima sem ruptura imediata de sentimento.',
    detailSummary: 'Util para antecipar picos quando numeros coincidem com pauta da BR-319.',
    detailRisk: 'Interpretacoes rivais dos numeros podem polarizar antes da resposta oficial.',
    detailAction: 'Responder com transparencia e dados verificaveis, nao com tom defensivo.',
    detailSignals: ['pesquisa local', 'tom neutro', 'antecipacao de pico'],
    recommendedActions: ['Nota com dados', 'Distribuir para aliados', 'Monitorar replicacao'],
    tags: ['Pesquisa', 'Interior', 'X/Twitter'],
  },
];

export const OMAR_ANALYTICS_INFLUENCERS: readonly OmarAnalyticsInfluencer[] = [
  { influencerId: 'luiz-bacci', influencerName: 'Luiz Bacci', handle: '@luizbacci', location: 'Brasilia, Brasil', platform: 'Instagram', sentiment: 'neutral', followers: '8.875.708', reach: '1,2 mi', mentions: 4, voiceShare: '18,42%', performance: '10/10', sentimentBars: [22, 54, 24], lastMention: 'Post citando Omar gerou 3.471 comentarios com alcance nacional.', expandedMentions: LUIZ_BACCI_DIRECTORY_MENTIONS },
  { influencerId: 'omar-aziz', influencerName: 'Omar Aziz', handle: '@omaraziz', location: 'Manaus, AM', platform: 'TikTok', sentiment: 'neutral', followers: '186.000', reach: '94 mil', mentions: 6, voiceShare: '14,28%', performance: '10/10', sentimentBars: [28, 48, 24], lastMention: 'Principal motor de interacoes em TikTok no recorte do mes.', expandedMentions: NORTE_DIGITAL_DIRECTORY_MENTIONS },
  { influencerId: 'cenarium', influencerName: 'Cenarium', handle: '@cenariumam', location: 'Manaus, AM', platform: 'TikTok', sentiment: 'neutral', followers: '412.000', reach: '124 mil', mentions: 5, voiceShare: '9,86%', performance: '9/10', sentimentBars: [24, 58, 18], lastMention: 'Ditou ritmo nos dias de pico com recortes sobre BR-319.', expandedMentions: CENARIUM_DIRECTORY_MENTIONS },
  { influencerId: 'convergente', influencerName: 'Portal O Convergente', handle: '@oconvergente', location: 'Manaus, AM', platform: 'TikTok', sentiment: 'neutral', followers: '198.000', reach: '86 mil', mentions: 4, voiceShare: '6,12%', performance: '8/10', sentimentBars: [20, 62, 18], lastMention: 'Portal regional em formato curto sobre pauta territorial.', expandedMentions: CONVERGENTE_DIRECTORY_MENTIONS },
  { influencerId: 'radar-amazonico', influencerName: 'Radar Amazonico', handle: '@radaramazonico', location: 'Manaus, AM', platform: 'X/Twitter', sentiment: 'neutral', followers: '64.000', reach: '42 mil', mentions: 3, voiceShare: '3,44%', performance: '7/10', sentimentBars: [18, 64, 18], lastMention: 'Pesquisas locais alteraram temperatura da conversa em 5 jun.', expandedMentions: BASE_ZONA_LESTE_DIRECTORY_MENTIONS },
];

export const OMAR_ANALYTICS_SITES: readonly OmarAnalyticsSite[] = [
  { siteId: 'portal-holanda', siteName: 'Portal do Holanda', siteKind: 'Noticias', sentiment: 'negative', visits: '1.634.606', mentions: 8, reach: '420 mil', performance: '10/10', sentimentBars: [62, 24, 14], lastHeadline: 'Questionamento sobre BR-319 puxou comentarios de cobranca.', expandedMentions: PORTAL_HOLANDA_DIRECTORY_MENTIONS },
  { siteId: 'amazonas-atual', siteName: 'Amazonas Atual', siteKind: 'Noticias', sentiment: 'neutral', visits: '755.000', mentions: 5, reach: '210 mil', performance: '9/10', sentimentBars: [28, 52, 20], lastHeadline: 'Cobertura institucional manteve tom de acompanhamento.', expandedMentions: RADIO_CIDADE_DIRECTORY_MENTIONS },
  { siteId: 'panorama-am', siteName: 'Blog Panorama AM', siteKind: 'Blog', sentiment: 'positive', visits: '195.000', mentions: 3, reach: '42 mil', performance: '7/10', sentimentBars: [16, 30, 54], lastHeadline: 'Aliados defendem vantagem na pauta territorial.', expandedMentions: PANORAMA_DIRECTORY_MENTIONS },
  { siteId: 'globo-am', siteName: 'globo.com Amazonas', siteKind: 'Web', sentiment: 'neutral', visits: '755.106.000', mentions: 2, reach: '1,2 mi', performance: '8/10', sentimentBars: [24, 56, 20], lastHeadline: 'Citou agenda estadual em cobertura de infraestrutura.', expandedMentions: LUIZ_BACCI_DIRECTORY_MENTIONS },
  { siteId: 'poder360', siteName: 'poder360.com.br', siteKind: 'Noticias', sentiment: 'negative', visits: '17.424.000', mentions: 1, reach: '380 mil', performance: '9/10', sentimentBars: [58, 30, 12], lastHeadline: 'Enquadramento nacional conectou sucessao e desgaste politico.', expandedMentions: NORTE_DIGITAL_DIRECTORY_MENTIONS },
];

export const OMAR_ANALYTICS_COMPARISON_METRICS: readonly OmarAnalyticsComparisonMetric[] = [
  { metricName: 'Mencoes', currentValue: OMAR_ANALYTICS_MONTH_TOTALS.mentionsLabel, previousValue: '1.658', deltaLabel: '+364', iconName: 'ti-message-circle' },
  { metricName: 'Alcance', currentValue: OMAR_ANALYTICS_MONTH_TOTALS.reachLabel, previousValue: '72,1 mil', deltaLabel: '+15,3 mil', iconName: 'ti-broadcast' },
  { metricName: 'Interacoes', currentValue: OMAR_ANALYTICS_MONTH_TOTALS.interactionsLabel, previousValue: '502 mil', deltaLabel: '+156 mil', iconName: 'ti-hand-click' },
  { metricName: 'Mencoes negativas', currentValue: '280', previousValue: '312', deltaLabel: '-32', iconName: 'ti-mood-sad' },
  { metricName: 'Mencoes positivas', currentValue: '670', previousValue: '486', deltaLabel: '+184', iconName: 'ti-mood-smile' },
];


export const OMAR_ANALYTICS_SECONDARY_MODULES: readonly OmarAnalyticsSecondaryModule[] = [
  { moduleName: 'settings', eyebrow: 'Configuracoes', title: 'Preferencias do monitoramento', body: 'Centralize idioma, periodo padrao, fontes ativas e sensibilidade de criticidade para o gabinete Omar Aziz.', actionLabel: 'Salvar preferencias' },
  { moduleName: 'emails', eyebrow: 'E-mails e alertas', title: 'Alertas quando a conversa muda', body: 'Dispare resumo para equipe quando TikTok, noticias ou portais locais cruzarem o limite de risco.', actionLabel: 'Configurar alerta' },
  { moduleName: 'assistant', eyebrow: 'Assistente IA', title: 'Pergunte ao painel antes de agir', body: 'Use o contexto de mencoes, influenciadores e comparacao para pedir um plano de resposta em linguagem de gabinete.', actionLabel: 'Abrir pergunta guiada' },
  { moduleName: 'team', eyebrow: 'Membros da equipe', title: 'Fila de trabalho do gabinete', body: 'Distribua leitura, resposta, relatorio e contato com base local entre os membros operacionais.', actionLabel: 'Adicionar responsavel' },
];

export const OMAR_ANALYTICS_FILTER_SETS: OmarAnalyticsFilterSets = {
  sentiments: [
    { optionValue: 'all', label: 'Todos' },
    { optionValue: 'negative', label: 'Critico' },
    { optionValue: 'neutral', label: 'Neutro' },
    { optionValue: 'positive', label: 'Positivo' },
  ],
  emotions: [
    { optionValue: 'raiva', label: 'Raiva' },
    { optionValue: 'apoio', label: 'Apoio' },
    { optionValue: 'atencao', label: 'Atencao' },
    { optionValue: 'confianca', label: 'Confianca' },
  ],
  languages: [
    { optionValue: 'pt-br', label: 'Portugues' },
    { optionValue: 'all', label: 'Todos os idiomas' },
  ],
  savedFilters: [
    { optionValue: 'manaus', label: 'Manaus critica' },
    { optionValue: 'pdf', label: 'PDF mentions' },
    { optionValue: 'br319', label: 'BR-319' },
  ],
  performanceBands: [
    { optionValue: 'high', label: 'Alta performance' },
    { optionValue: 'medium', label: 'Media performance' },
  ],
  siteKinds: [
    { optionValue: 'all', label: 'Todos os sites' },
    { optionValue: 'Noticias', label: 'Noticias' },
    { optionValue: 'Blog', label: 'Blogs' },
    { optionValue: 'Web', label: 'Web' },
  ],
};
